import { Model, modelRegistry } from './Model';
import { TaxRate, ProductCategory, ProductIngredient } from '../models';
import { PrintRoleType } from '../types';
import ApiService from '../services/ApiService';
import Money from '../utilities/Money';
import Store from '../wrappers/Store';
import { I18n } from '../wrappers/I18n';
import moment from 'moment-timezone';
export class Product extends Model {
    static entity = 'products';
    static get PRINTING_SECTORS() {
        return [
            PrintRoleType.bar,
            PrintRoleType.kitchen,
            PrintRoleType.sector_1,
            PrintRoleType.sector_2
        ];
    }
    id = null;
    name = '';
    code = null;
    barcode = null;
    gtin = null;
    is_goods = true;
    is_measured = false;
    is_made = false;
    is_sold = true;
    excluded_from_delivery = false;
    description = null;
    image_url = null;
    dietary_tags = null;
    alcohol_percentage = null;
    requires_quantity = false;
    category_id = null;
    type = null;
    printing_sector = null;
    selling_price = '0.0000';
    purchase_price = null;
    tax_rate_id = null;
    count = '0.000';
    /**
     * Which figure `count` currently holds on THIS terminal (§2.2): the id of the
     * register's own warehouse when `count` is that warehouse's row, `null` when
     * `count` is the venue total. The server stamps it on every POS product
     * payload; it is never sent back (no request in app-core carries it) and it is
     * `null` for every product on a tenant without the warehouses module, which is
     * what keeps `Product.update` behaving exactly as it did before the feature.
     */
    stock_warehouse_id = null;
    /**
     * The product's SALES PIN (`products.warehouse_id`, MultiWarehouse.md §2.7 /
     * MultiWarehouse.md US-6) — step 1 of the sale-resolution chain. It rides the ADMIN product
     * payload only; the server hides it from every POS payload (§2.2), so on a POS
     * terminal this always stays at its `null` default. Nothing in app-core reads or
     * sends it — the admin product form is its one consumer.
     */
    warehouse_id = null;
    min_count = '0.000';
    selling_count = '0.000';
    unit_of_measure = null;
    unit_of_measure_sold = null;
    net_quantity = '0.000';
    modified_at = null;
    created_at = null;
    updated_at = null;
    prices = [];
    category;
    taxRate;
    price_difference = null;
    ingredients;
    static fields() {
        return {
            id: this.attr(null),
            name: this.attr(''),
            code: this.attr(null),
            barcode: this.attr(null),
            gtin: this.attr(null),
            is_goods: this.attr(true),
            is_measured: this.attr(false, value => !!value),
            is_made: this.attr(false, value => !!value),
            is_sold: this.attr(true, value => !!value),
            excluded_from_delivery: this.attr(false, value => !!value),
            description: this.attr(null),
            image_url: this.attr(null),
            dietary_tags: this.attr(null),
            alcohol_percentage: this.attr(null, value => (value === null || value === '' ? null : Number(value))),
            requires_quantity: this.attr(false, value => !!value),
            category_id: this.attr(null),
            type: this.attr(null),
            printing_sector: this.attr(null),
            selling_price: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            tax_rate_id: this.attr(null),
            count: this.attr('0.000', value => this.round(value, 3).toFixed(3)),
            stock_warehouse_id: this.attr(null, value => (value === null || value === undefined || value === '' ? null : Number(value))),
            warehouse_id: this.attr(null, value => (value === null || value === undefined || value === '' ? null : Number(value))),
            min_count: this.attr(null, value => (value === null ? null : this.round(value, 3).toFixed(3))),
            selling_count: this.attr('0.000', value => this.round(value, 3).toFixed(3)),
            purchase_price: this.attr(null, value => value ? this.round(value, 4).toFixed(4) : null),
            unit_of_measure: this.attr(null),
            unit_of_measure_sold: this.attr(null),
            net_quantity: this.attr('0.000', value => value ? this.round(value, 3).toFixed(3) : null),
            modified_at: this.attr(null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            prices: this.attr([], (value) => {
                return value.map(item => {
                    return { id: item.id, name: item.name, price: this.round(item.price, 4).toFixed(4) };
                });
            }),
            category: this.attr(undefined, value => value ? new ProductCategory(value) : undefined),
            taxRate: this.attr(undefined, value => value ? new TaxRate(value) : undefined),
            price_difference: this.attr(null, value => value ? this.round(value, 4).toFixed(4) : null),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    /**
     * Fetch new products from server
     */
    static async fetchLatest() {
        const lastUpdatedAt = Product.getLastUpdatedAt();
        const params = { last_updated_at: lastUpdatedAt ? lastUpdatedAt.utc().format('YYYY-MM-DD HH:mm:ss') : null };
        const response = await ApiService.get('/products/latest', { params: params });
        Product.insert({ data: response.data.products });
    }
    /**
     * Apply warehouse-tagged stock counts — the ONE place the rule lives.
     *
     * Every producer of `product_counts` goes through here unchanged: the order
     * create/update/cancel/delete and sync responses (`Order.syncExec`,
     * `Order.processQrOrder`), the locally built offline and sync-exception
     * entries (`Order.getUpdatedProductCounts`), and a peer terminal's
     * `syncTerminals` broadcast (`AppEvent.handleSyncTerminalsAdditional`).
     *
     * Per entry, against the STORED product (MultiWarehouse.md §2.4):
     *
     * | stored `stock_warehouse_id` | entry                        | result                          |
     * |-----------------------------|------------------------------|---------------------------------|
     * | `null`                      | any                          | `count = entry.count`           |
     * | `W`                         | `entry.warehouse_id === W`   | `count = entry.warehouse_count` |
     * | `W`                         | `entry.warehouse_id !== W`   | ignored — that movement did not touch the warehouse this terminal displays |
     *
     * A product MAY appear in more than one entry, one per warehouse it moved in
     * during the order change (§2.3): each entry is applied independently and is
     * never deduped by id. An entry whose applicable figure is `null` OR absent is
     * skipped rather than written — the tagged local entries omit `count`
     * entirely, and a peer displaying the other figure must keep its last server
     * value instead of blanking the count. The authoritative figures arrive on the
     * next drain.
     *
     * With the warehouses module off, every stored product is null-tagged and every
     * entry carries `warehouse_id: null`, so this reduces to `count = entry.count`
     * — today's behaviour exactly, legacy `{ id, count }` payloads included.
     */
    static update(counts) {
        const entries = Array.isArray(counts) ? counts : [counts];
        const updates = [];
        for (const entry of entries) {
            if (!entry || entry.id === null || entry.id === undefined)
                continue;
            // The STORED product decides: only it knows which figure this terminal
            // is currently displaying. An entry for a product we do not hold is
            // dropped (the apps' models store ignores unknown keys on update too).
            const product = Product.find(entry.id);
            if (!product)
                continue;
            const tag = product.stock_warehouse_id;
            const value = tag === null
                ? entry.count
                : entry.warehouse_id === tag ? entry.warehouse_count : undefined;
            // `undefined` and `null` alike mean "no figure applies": a tagged local
            // entry omits `count`, a legacy payload omits both new fields,
            // and the server nulls what does not apply. Never write either — the
            // count mutator would turn them into '0.000'.
            if (value === null || value === undefined)
                continue;
            updates.push({ id: entry.id, count: value });
        }
        if (!updates.length)
            return;
        // One store write: persistence, reactivity and the partial-merge semantics
        // stay exactly as the generic Model.update provided them.
        super.update(updates);
    }
    /**
     * Merge product records another terminal broadcast (its offline drain, a price edit). Each
     * carries the SENDER's stock view — its register's warehouse row, or the venue total — so the
     * rest of the record is merged over this terminal's own count and tag, and the figure goes
     * through update()'s rule. Without warehouses every tag is null and the record lands whole.
     */
    static mergeBroadcast(records) {
        Product.insert({
            data: records.map(record => {
                const stored = record.id === null ? null : Product.find(record.id);
                return stored ? { ...record, count: stored.count, stock_warehouse_id: stored.stock_warehouse_id } : record;
            })
        });
        const counts = [];
        for (const record of records) {
            if (record.id === null)
                continue;
            const figure = record.count === null || record.count === undefined ? null : Number(record.count);
            counts.push(record.stock_warehouse_id === null || record.stock_warehouse_id === undefined
                ? { id: record.id, count: figure, warehouse_id: null, warehouse_count: null }
                : { id: record.id, warehouse_id: record.stock_warehouse_id, warehouse_count: figure });
        }
        Product.update(counts);
    }
    /**
     * Make a copy of the Product.
     */
    $duplicate() {
        return new Product(this.$toJson());
    }
    getTaxRate() {
        return TaxRate.find(this.tax_rate_id);
    }
    getIngredients() {
        return ProductIngredient.all().filter(pi => pi.super_product_id === this.id);
    }
    getCount() {
        return parseFloat(this.count);
    }
    getSellingCount() {
        return parseFloat(this.selling_count);
    }
    getSellingPrice(pricelistId = 0) {
        if (pricelistId) {
            const pricelist = this.prices.find(pricelist => pricelist.id === pricelistId);
            if (pricelist) {
                return Money.parse(pricelist.price);
            }
        }
        return Money.parse(this.selling_price);
    }
    getPurchasePrice() {
        if (!this.purchase_price)
            return null;
        return Money.parse(this.purchase_price);
    }
    getPurchasePriceBeforeTax() {
        const purchasePrice = this.getPurchasePrice();
        if (!purchasePrice)
            return null;
        const taxRate = this.getTaxRate();
        if (!taxRate) {
            return purchasePrice;
        }
        const reverseCalculation = !!Store.get('tenant')?.venue?.settings.reverse_calculation;
        if (reverseCalculation) {
            return purchasePrice;
        }
        return purchasePrice.divide(1 + taxRate.getRate() / 100);
    }
    getPurchasePriceAfterTax() {
        const purchasePrice = this.getPurchasePrice();
        if (!purchasePrice)
            return null;
        const taxRate = this.getTaxRate();
        if (!taxRate) {
            return purchasePrice;
        }
        const reverseCalculation = !!Store.get('tenant')?.venue?.settings.reverse_calculation;
        if (reverseCalculation) {
            return purchasePrice.multiply(1 + taxRate.getRate() / 100);
        }
        return purchasePrice;
    }
    getPurchaseValueBeforeTax() {
        const priceBeforeTax = this.getPurchasePriceBeforeTax();
        if (!priceBeforeTax)
            return null;
        return priceBeforeTax.multiply(this.getCount());
    }
    getPurchaseValueAfterTax() {
        const priceAfterTax = this.getPurchasePriceAfterTax();
        if (!priceAfterTax)
            return null;
        return priceAfterTax.multiply(this.getCount());
    }
    getPriceBeforeTax(pricelistId = 0) {
        const taxRate = this.getTaxRate();
        if (!taxRate) {
            return this.getSellingPrice(pricelistId);
        }
        const reverseCalculation = !!Store.get('tenant')?.venue?.settings.reverse_calculation;
        if (reverseCalculation) {
            return this.getSellingPrice(pricelistId);
        }
        return this.getSellingPrice(pricelistId).divide(1 + taxRate.getRate() / 100);
    }
    getPriceAfterTax(pricelistId = 0) {
        const taxRate = this.getTaxRate();
        if (!taxRate) {
            return this.getSellingPrice(pricelistId);
        }
        const reverseCalculation = !!Store.get('tenant')?.venue?.settings.reverse_calculation;
        if (reverseCalculation) {
            return this.getSellingPrice(pricelistId).multiply(1 + taxRate.getRate() / 100);
        }
        return this.getSellingPrice(pricelistId);
    }
    getSellingValueBeforeTax() {
        return this.getPriceBeforeTax().multiply(this.getCount());
    }
    getSellingValueAfterTax() {
        return this.getPriceAfterTax().multiply(this.getCount());
    }
    getNetQuantity() {
        return this.net_quantity ? parseFloat(this.net_quantity) : null;
    }
    getQuantityUnitPrice() {
        const netQuantity = this.getNetQuantity();
        if (!netQuantity)
            return Money.parse(0);
        return this.getPriceAfterTax().divide(netQuantity);
    }
    /**
     * Per-item predicate for typeahead/autocomplete pickers that expose a
     * `filter(item, query)` hook (e.g. cod-typeahead, Vuetify v-autocomplete).
     * Delegates to `search` against a single-element list so the matched fields
     * and matching rules stay identical to the main product search — there is no
     * second copy of the field list to drift out of sync. Quantity and
     * weighted-barcode parsing are disabled (meaningless for a per-item filter).
     * An empty query matches everything.
     */
    static matchesQuery(product, query) {
        if (!query || !query.trim())
            return true;
        return Product.search(query, [product], {
            limit: 1,
            disableQuantity: true,
            disableWeightedBarcodes: true,
        }).products.length > 0;
    }
    static search(query, products = null, options) {
        const { limit = 10, disableQuantity = false, disableWeightedBarcodes = false, disableExactSearch = false } = options;
        const queryText = query?.toLowerCase();
        if (!query) {
            return {
                products: []
            };
        }
        if (!products) {
            products = Product.all().filter(product => product.is_sold);
        }
        // quantity search
        const isQuantitySearch = !disableQuantity ? /^-?[0-9]{1,}\.?[0-9]{0,3}\*/.test(query) : false;
        if (isQuantitySearch) {
            return {
                products: [],
                quantity: parseFloat(query.split('*')[0]),
                query: query.split('*')[1]
            };
        }
        // weighted barcode search
        if (!disableWeightedBarcodes) {
            const barcodePatterns = Store.get('tenant').venue?.settings?.barcode_pattern?.split('|') ?? [];
            for (let i = 0; i < barcodePatterns.length; i++) {
                const barcodePattern = barcodePatterns[i].split(',');
                const minBarcodeLength = barcodePattern[0].length + parseInt(barcodePattern[1]) + parseInt(barcodePattern[2]);
                if (queryText.startsWith(barcodePattern[0]) && queryText.length >= minBarcodeLength) {
                    const codeInBarcode = queryText.substr(barcodePattern[0].length, parseInt(barcodePattern[1]));
                    const product = products.find(product => {
                        const code = product.code ? product.code.toLowerCase().replace(/^0+/, '') : '';
                        return code && code === codeInBarcode.replace(/^0+/, '');
                    });
                    if (product) {
                        const quantityDecimals = !isNaN(+barcodePattern[3]) ? parseInt(barcodePattern[3]) : 3;
                        const quantity = parseFloat(queryText.substr(barcodePattern[0].length + parseInt(barcodePattern[1]), parseInt(barcodePattern[2]))) / Math.pow(10, quantityDecimals);
                        return {
                            products: [product],
                            quantity: quantity
                        };
                    }
                }
            }
        }
        if (!disableExactSearch) {
            // A whole name is not narrowed to its product: it leads the list below, followed by
            // every other product whose name contains it ("Hleb" also lists "Domaći hleb").
            // exact search by barcode
            let productsMatched = products.filter(product => product.barcode && product.barcode.toLowerCase() === queryText);
            if (productsMatched.length) {
                return {
                    products: productsMatched.splice(0, limit)
                };
            }
            // exact search by code
            productsMatched = products.filter(product => product.code && product.code.toLowerCase().replace(/^0+/, '') === queryText.replace(/^0+/, ''));
            if (productsMatched.length) {
                return {
                    products: productsMatched.splice(0, limit)
                };
            }
            // exact search by gtin
            productsMatched = products.filter(product => product.gtin && product.gtin.toLowerCase() === queryText);
            if (productsMatched.length) {
                return {
                    products: productsMatched.splice(0, limit)
                };
            }
        }
        const exact = [];
        const partial = [];
        const doesMatch = (product) => {
            const name = product.name ? product.name.toLowerCase() : '';
            const code = product.code ? product.code.toLowerCase() : '';
            const barcode = product.barcode ? product.barcode.toLowerCase() : '';
            const gtin = product.gtin ? product.gtin.toLowerCase() : '';
            const isExact = name === queryText
                || code === queryText
                || barcode === queryText
                || gtin === queryText;
            const isPartial = name.indexOf(queryText) > -1
                || code.indexOf(queryText) > -1
                || barcode.indexOf(queryText) > -1
                || gtin.indexOf(queryText) > -1;
            return { exact: isExact, partial: isPartial };
        };
        for (const product of products) {
            if (exact.length >= limit)
                break;
            const match = doesMatch(product);
            if (match.exact) {
                exact.push(product);
            }
        }
        for (const product of products) {
            if (exact.length + partial.length >= limit)
                break;
            const match = doesMatch(product);
            if (match.partial && !match.exact) {
                partial.push(product);
            }
        }
        return {
            products: [...exact, ...partial]
        };
    }
    /**
     * Get last update of products from server — null when no stored product carries one
     * (none yet, or only ones written outside a load), so the next load asks for every product
     */
    static getLastUpdatedAt() {
        const loadedAt = Product.all().filter(p => p.updated_at).map(p => moment(p.updated_at).valueOf());
        return loadedAt.length ? moment(Math.max(...loadedAt)) : null;
    }
    /**
     * Products that reach the store outside a product load (a save's answer, the /sync-data
     * drain), for storing and for the other terminals: without `updated_at`. The newest stored
     * one is where the next load starts (getLastUpdatedAt), and theirs would skip the product
     * changes made elsewhere since this terminal last loaded. A held product keeps its own time
     * (insert merges); one not held has none until the next load fetches it.
     */
    static withoutLoadWatermark(records) {
        return records.map(({ updated_at: _updatedAt, ...record }) => record);
    }
    getUnitOfMeasure() {
        if (!this.unit_of_measure)
            return null;
        const translationKey = `products.unit_of_measure.${this.unit_of_measure}`;
        const translatedValue = I18n.t(translationKey);
        return translatedValue !== translationKey ? translatedValue : this.unit_of_measure;
    }
    getUnitOfMeasureSold() {
        if (!this.unit_of_measure_sold)
            return null;
        const translationKey = `products.unit_of_measure.${this.unit_of_measure_sold}`;
        const translatedValue = I18n.t(translationKey);
        return translatedValue !== translationKey ? translatedValue : this.unit_of_measure_sold;
    }
    /**
     * Whether this product's stock is kept by inventory accounting: a sold, non-made good on a tenant
     * with the inventory_accounting module (the module is the one gate — InventoryTransfers.md
     * AD-7; the legacy use_inventory_accounting venue setting is not read).
     */
    usesInventoryAccounting() {
        if (!this.is_goods || this.is_made || !this.is_sold)
            return false;
        return !!Store.get('modules')?.inventory_accounting;
    }
}
export default Product;
modelRegistry.register('Product', Product);
//# sourceMappingURL=Product.js.map